// Owns the ONE settings dialog for the whole app.
//
// Mounted above <Routes> in App.tsx, deliberately: the landing surfaces
// (`/`, `/landing-v2`) sit OUTSIDE <Layout>, so anything mounted
// there could only ever serve the dashboard. Hoisting also means a single modal
// instance and a single deep-link parser rather than one copy per navbar.
//
// Every surface (layout/Header, landing-v2/NavbarV2) opens it
// through `useSettingsModal().open(sectionId)`.

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from '../../../contexts/AuthContext'
import { useFlagsContext } from '../../../contexts/FlagsProvider'
import { useBooleanFlagValue, useStringFlagValue } from '../../../hooks/useBooleanFlagValue'
import { canSeeBilling } from '../../../features/billing/billingAccess'
import { canViewTeamCredits } from '../../../features/billing/creditVisibility'
import { useActiveTeam } from '../../../contexts/ActiveTeamContext'
import { useTeamsEnabled } from '../../../features/teams/useTeamsEnabled'
import { createNadDebug } from '../../../utils/debugLog'
import SettingsModal from './SettingsModal'
import CheckoutResultDialog from '../../../features/billing/CheckoutResultDialog'
import { readCheckoutResultState } from '../../../features/billing/checkoutResultState'
import {
  SettingsModalContext,
  type SettingsEntryPoint,
  type SettingsModalContextValue,
  type SettingsOpenOptions,
  type SettingsTeamsView,
} from './settingsModalContextRaw'
import {
  clearConsumedSettingsParams,
  hasConsumableSettingsParams,
  parseSettingsDeepLink,
  type SettingsDeepLinkIntent,
} from './settingsDeepLink'
import { DEFAULT_SETTINGS_SECTION, type SettingsSectionId } from './settingsSections'

const log = createNadDebug('dw:settings-modal')

export function useSettingsModal(): SettingsModalContextValue {
  const ctx = useContext(SettingsModalContext)
  if (ctx === null) {
    throw new Error('useSettingsModal must be used within a SettingsModalProvider')
  }
  return ctx
}

export default function SettingsModalProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth()
  const { isLoading: flagsLoading } = useFlagsContext()
  const { activeTeam, isLoading: teamsLoading } = useActiveTeam()
  const teamsEnabled = useTeamsEnabled()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const [isOpen, setIsOpen] = useState(false)
  const [activeSection, setActiveSection] = useState<SettingsSectionId>(DEFAULT_SETTINGS_SECTION)
  const [payment, setPayment] = useState<SettingsDeepLinkIntent['payment']>(undefined)
  const [entryPoint, setEntryPoint] = useState<SettingsEntryPoint>('user_menu')
  const [teamsView, setTeamsView] = useState<SettingsTeamsView | undefined>(undefined)
  const [billingTeamId, setBillingTeamId] = useState<string | undefined>(undefined)

  const billingSandboxEnabled = useBooleanFlagValue('billing_sandbox_enabled', false)
  const billingAudience = useStringFlagValue('billing_audience', 'meta_only')
  const audienceAllowsBilling = canSeeBilling(user, undefined, billingAudience)
  // Kept SEPARATE on purpose. The audience predicate is decidable as soon as
  // auth settles; the flag is not. Folding them together makes the combined
  // value indistinguishable from "denied" during flag load, which silently
  // defeats the wait below — see the comment there.
  const billingVisible = audienceAllowsBilling && billingSandboxEnabled && (
    !teamsEnabled || canViewTeamCredits(activeTeam)
  )

  const open = useCallback(
    (section: SettingsSectionId = DEFAULT_SETTINGS_SECTION, options?: SettingsOpenOptions) => {
      log({
        event: 'open',
        trace_id: 'settings-modal:open',
        section,
        teams_view: options?.teamsView ?? null,
      })
      if (section !== 'usage-credits' && location.pathname.startsWith('/settings/billing')) navigate('/dashboard', { replace: true })
      setEntryPoint('user_menu')
      setActiveSection(section)
      // Always overwritten, so a plain open() never inherits a stale request.
      setTeamsView(options?.teamsView)
      setBillingTeamId(options?.billingTeamId)
      setIsOpen(true)
    },
    [location.pathname, navigate],
  )

  const clearTeamsView = useCallback(() => setTeamsView(undefined), [])

  const close = useCallback(() => {
    log({ event: 'close', trace_id: 'settings-modal:close' })
    setIsOpen(false)
    if (location.pathname.startsWith('/settings/billing')) navigate('/dashboard', { replace: true })
  }, [location.pathname, navigate])

  const clearPayment = useCallback(() => {
    setPayment(undefined)
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('payment')
        next.delete('order')
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  // Deep links. This is an effect on the SEARCH STRING, not a mount-once read:
  // returning from /checkout is an SPA navigation and the provider never
  // unmounts, so a mount-only parse would silently miss it.
  //
  // It always waits for auth, because a gated section has to be evaluated
  // against a resolved user.
  //
  // It waits for FLAGS only when the requested section is actually flag-gated.
  // Blocking every deep link on the flag snapshot would be wrong twice over:
  // `apiRequest` retries with backoff, so a slow or failing /config/flags would
  // visibly delay opening Profile; and the signed-out case never fetches at
  // all. Scoping the wait to the section that needs it keeps the guarantee
  // where it matters — `useBooleanFlagValue` cannot tell "not loaded" from
  // "off", so acting early on a gated link would fall back to the default
  // section AND clear the params, making the link unrecoverable.
  const appliedRef = useRef<string | null>(null)
  useEffect(() => {
    if (authLoading) return

    const key = searchParams.toString()
    if (appliedRef.current === key) return

    const intent = parseSettingsDeepLink(searchParams)
    if (intent === null) {
      appliedRef.current = key
      return
    }

    // Both audience and purchase visibility depend on the flag snapshot. Wait
    // even when the current default audience rejects this user because the
    // resolved billing_audience flag can widen from meta_only to all.
    if (intent.section === 'usage-credits' && flagsLoading) return
    if (intent.section === 'usage-credits' && teamsEnabled && teamsLoading) return

    // A gated section reached by deep link falls back rather than 404s.
    const section =
      intent.section === 'usage-credits' && !billingVisible
        ? DEFAULT_SETTINGS_SECTION
        : intent.section

    log({
      event: 'deep-link',
      trace_id: 'settings-modal:deep-link',
      requested: intent.section,
      section,
      payment: intent.payment?.status ?? null,
    })

    setEntryPoint('settings_deeplink')
    setActiveSection(section)
    setIsOpen(true)
    if (intent.payment) setPayment(intent.payment)

    // Consume ONLY our own keys — the dashboard owns tab/page/q on this same
    // query string. payment/order are left for clearPayment() so the success
    // panel isn't wiped by the very effect that opened it.
    if (hasConsumableSettingsParams(searchParams)) {
      const next = clearConsumedSettingsParams(searchParams)
      appliedRef.current = next.toString()
      setSearchParams(next, { replace: true })
    } else {
      appliedRef.current = key
    }
  }, [
    searchParams,
    authLoading,
    flagsLoading,
    teamsEnabled,
    teamsLoading,
    audienceAllowsBilling,
    billingVisible,
    setSearchParams,
  ])

  useEffect(() => {
    if (location.pathname !== '/settings/billing' && location.pathname !== '/settings/billing/add-credits') return
    if (authLoading || flagsLoading || (teamsEnabled && teamsLoading)) return
    setActiveSection(billingVisible ? 'usage-credits' : DEFAULT_SETTINGS_SECTION)
    setEntryPoint('settings_deeplink')
    setIsOpen(true)
    if (!billingVisible) navigate('/dashboard', { replace: true })
  }, [location.pathname, authLoading, flagsLoading, teamsEnabled, teamsLoading, billingVisible, navigate])

  const checkoutResult = readCheckoutResultState(location.state)
  const dismissCheckoutResult = useCallback(() => {
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null })
  }, [location.pathname, location.search, location.hash, navigate])

  const value = useMemo<SettingsModalContextValue>(
    () => ({
      isOpen,
      activeSection,
      open,
      close,
      payment,
      clearPayment,
      entryPoint,
      teamsView,
      clearTeamsView,
      billingTeamId,
    }),
    [isOpen, activeSection, open, close, payment, clearPayment, entryPoint, teamsView, clearTeamsView, billingTeamId],
  )

  return (
    <SettingsModalContext.Provider value={value}>
      {children}
      <SettingsModal />
      {isOpen && billingVisible && checkoutResult && <CheckoutResultDialog key={`${user?.id}:${checkoutResult.orderId}:${checkoutResult.outcome}`} result={checkoutResult} onClose={dismissCheckoutResult} />}
    </SettingsModalContext.Provider>
  )
}
